import { ToastType } from 'components/Modal/ToastManager';

interface EnvConstantSet {
  envName: string;

  // service url
  webClientUrl: string;
  webHostUrl: string;
  gqlApiUrl: string;
  identityApiUrl: string;

  // third party lib
  iamport: string;
  googleMapApiKey: string;
  kakaoAppKey: string;
  facebookAppKey: string;
  hackleKey: string;
  gaTrackingId: string;
  s3BaseURL: string;

  // tracking lib
  hjId?: number; // beta 환경과 prd 환경에만 적용합니다.
  hjSv?: number;

  //talkplus
  talkPlusAppId: string;
}

declare global {
  interface Window {
    __ENV_STATE__: string;
    REACT_APP_ENV: string;
    __INIT_AUTHORIZATION__: string;
    kakao: any;
    FB: any;
    IMP: any;
    webkit?: any;
    Android?: any;
    google: any;
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    showToast: (message: string, type: ToastType, duration?: number) => void;
    handlePageBackwardNavigate(): void;
    handleOnClosePage(): void;
    handleSetAuthorization(param: { authToken: string }): void;
    handleSetHostAdminStart(): void;
    handleSetIsHostAdminApp(isHostAdmin: boolean): void;
    handleImageUpload(link: { link: string }): void;
    handleSettlementIdentityUpload(link: { link: string }): void;
    handleImageLoading(): void;
    handleMobileModalClose: () => void;
    handleOpenLoading: () => void;
    handleCloseLoading: () => void;
    handleResizeViewPort(keyboardHeight: { keyboardHeight: string }): void;
    handleResizeOnBlur(): void;
  }

  let Kakao: {
    init: any;
    Auth: any;
    Link: any;
    isInitialized: any;
    cleanup: any;
  };
}

const IAMPORT_KEY_DEV = 'imp18257751';
const IAMPORT_KEY_PROD = 'imp17508840';

// GA4(Google Analytics 4) 측정 ID. 핵클과 동일한 이벤트를 GA4에도 미러링하는 데 사용한다.
// staging 은 productionEnv 를 펼친 뒤 DEV ID 로 덮어쓴다(핵클 키 정책과 동일).
const GA_TRACKING_ID = {
  DEV: 'G-9RYW1FG3Q9',
  PROD: 'G-7LQVXDJBSD',
};

const GOOGLE_MAP_API_KEY_DEV = 'AIzaSyCzSyopq0__ErDzrnOC5D9Jn39yut1GsFc'; // TODO: 주의. 사용 가능 도메인 제한이 없는 API key. 가급적 사용을 지양해야 합니다.
const GOOGLE_MAP_API_KEY_PROD = 'AIzaSyB0q9cEbGkf4vYW-ZEEthcWu0jyO-Lqrv8';

const KAKAO_APP_KEY_DEV = 'b2774bb606e7431f85f5604e11fb127a';
const KAKAO_APP_KEY_PROD = '90a1878dbc5b1c03f9c7db732ac4b97f';

const FACEBOOK_APP_KEY_DEV = '544244172382640';
const FACEBOOK_APP_KEY_PROD = '488654004608324';

const TALKPLUS_APP_ID = {
  DEV: '933d87b5-ea2b-4922-9f1b-11bd40a8a03b',
  STAGING: '8b5705aa-accf-48bd-aeb6-6b8848948063',
  PROD: '8b5705aa-accf-48bd-aeb6-6b8848948063',
};

const HACKLE_KEY = {
  DEV: 'q2pNFlua5pagOfXc4evwTozPKnmyZzCA',
  PROD: 'Ut0mL5sSw4b6VmHJcQQM682DpzmwK63M',
};

const S3_BASE_URL = {
  DEV: 'dev',
  PROD: 'prod',
};

// staging | prod
const productionEnv: EnvConstantSet = {
  envName: 'production',
  webClientUrl: 'https://www.frip.co.kr',
  webHostUrl: 'https://new.frip.host',
  gqlApiUrl: 'https://gql.frip.co.kr/graphql',
  identityApiUrl: 'https://identity.frip.co.kr',
  iamport: IAMPORT_KEY_PROD,
  googleMapApiKey: GOOGLE_MAP_API_KEY_PROD,
  kakaoAppKey: KAKAO_APP_KEY_PROD,
  facebookAppKey: FACEBOOK_APP_KEY_PROD,
  hjId: 1637843,
  hjSv: 6,

  hackleKey: HACKLE_KEY.PROD,
  gaTrackingId: GA_TRACKING_ID.PROD,
  talkPlusAppId: TALKPLUS_APP_ID.PROD,
  s3BaseURL: S3_BASE_URL.PROD,
};

const environmentVariables: { [key: string]: EnvConstantSet } = {
  development: {
    envName: 'development',
    webClientUrl: 'https://dev.frip.co.kr',
    webHostUrl: 'https://new-dev.frip.host',
    gqlApiUrl: 'https://gql-dev.frip.co.kr/graphql',
    identityApiUrl: 'https://identity-dev.frip.co.kr',
    iamport: IAMPORT_KEY_DEV,
    googleMapApiKey: GOOGLE_MAP_API_KEY_DEV,
    kakaoAppKey: KAKAO_APP_KEY_DEV,
    facebookAppKey: FACEBOOK_APP_KEY_DEV,
    hackleKey: HACKLE_KEY.DEV,
    gaTrackingId: GA_TRACKING_ID.DEV,
    talkPlusAppId: TALKPLUS_APP_ID.DEV,
    s3BaseURL: S3_BASE_URL.DEV,
  },

  staging: {
    ...productionEnv,
    envName: 'staging',
    webClientUrl: 'https://staging.frip.co.kr',
    webHostUrl: 'https://new-staging.frip.host',
    gqlApiUrl: 'https://gql-staging.frip.co.kr/graphql',
    identityApiUrl: 'https://identity-staging.frip.co.kr',
    googleMapApiKey: GOOGLE_MAP_API_KEY_DEV,
    hackleKey: HACKLE_KEY.DEV,
    gaTrackingId: GA_TRACKING_ID.DEV,
    talkPlusAppId: TALKPLUS_APP_ID.STAGING,
    s3BaseURL: S3_BASE_URL.PROD,
  },
  production: productionEnv,
};

export function getEnvConstant<T extends keyof EnvConstantSet>(key: T) {
  const systemEnv =
    window.REACT_APP_ENV === '___ENV_STATE___'
      ? 'development'
      : window.REACT_APP_ENV;

  if (systemEnv === undefined) {
    throw new Error('NODE_ENV 가 정의되지 않았습니다.');
  }

  const constantSet = environmentVariables[systemEnv];

  if (constantSet === undefined) {
    throw new Error(`허용하지 않는 NODE_ENV 입니다: ${systemEnv}`);
  }

  return constantSet[key];
}

export const IS_PRODUCTION =
  process.env.NODE_ENV === 'production' &&
  process.env.REACT_APP_ENV === 'production'; // 사용자가 실제로 사용하는 환경이다.
